import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { seasonalPoojaService } from '../../services/seasonalPoojaService';
import { FamilyMember } from '../../services/seasonalPoojaService';
import { trackEventWithUTM, trackBeginCheckout } from '../../utils/analytics';
import './SeasonalPoojaPayment.css';

interface PaymentLocationState {
  bookingUuid: string;
  occasionName: string;
  preferredDate: string;
  primaryMember: {
    firstName: string;
    lastName: string;
    gotra?: string;
  };
  familyMembers: FamilyMember[];
  finalAmount: number;
  pricingData: any;
  razorpayKeyId: string;
  selectedPlan?: string;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const SeasonalPoojaPayment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as PaymentLocationState;

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Redirect if no booking data
    if (!state || !state.bookingUuid) {
      console.log('❌ No booking data found, redirecting to booking page');
      navigate('/seasonal-pooja');
      return;
    }

    console.log('✅ Payment page loaded with booking data:', state);

    // Pre-fill customer name from primary member
    if (state.primaryMember) {
      setCustomerName(`${state.primaryMember.firstName} ${state.primaryMember.lastName}`);
    }

    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [state, navigate]);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getAllMembers = (): string[] => {
    const members: string[] = [];
    
    if (state.primaryMember) {
      members.push(`${state.primaryMember.firstName} ${state.primaryMember.lastName}`);
    }
    
    if (state.familyMembers && state.familyMembers.length > 0) {
      state.familyMembers.forEach(member => {
        members.push(`${member.firstName} ${member.lastName}`);
      });
    }
    
    return members;
  };

  // Comprehensive validation functions
  const validateCustomerName = (name: string): { isValid: boolean; error?: string } => {
    const trimmed = name.trim();
    if (!trimmed) return { isValid: false, error: 'Customer name is required' };
    if (trimmed.length > 100) return { isValid: false, error: 'Customer name must be 100 characters or less' };
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return { isValid: false, error: 'Customer name should contain only letters and spaces' };
    return { isValid: true };
  };

  const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    const trimmed = email.trim();
    if (!trimmed) return { isValid: false, error: 'Email is required' };
    if (trimmed.length > 254) return { isValid: false, error: 'Email must be 254 characters or less' };
    
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) return { isValid: false, error: 'Please enter a valid email address (e.g., user@example.com)' };
    
    return { isValid: true };
  };

  const validatePhone = (phone: string): { isValid: boolean; error?: string } => {
    const cleaned = phone.replace(/\s/g, '');
    if (!cleaned) return { isValid: false, error: 'Phone number is required' };
    
    // Check if only digits
    if (!/^\d+$/.test(cleaned)) return { isValid: false, error: 'Phone number should contain only digits' };
    
    // Check if exactly 10 digits
    if (cleaned.length !== 10) return { isValid: false, error: 'Phone number must be exactly 10 digits' };
    
    // Check if starts with valid digit (6-9 for Indian mobile numbers)
    if (!/^[6-9]/.test(cleaned)) return { isValid: false, error: 'Phone number must start with 6, 7, 8, or 9' };
    
    return { isValid: true };
  };

  const validateForm = (): boolean => {
    // Validate customer name
    const nameValidation = validateCustomerName(customerName);
    if (!nameValidation.isValid) {
      alert(nameValidation.error);
      return false;
    }

    // Validate email
    const emailValidation = validateEmail(customerEmail);
    if (!emailValidation.isValid) {
      alert(emailValidation.error);
      return false;
    }

    // Validate phone
    const phoneValidation = validatePhone(customerPhone);
    if (!phoneValidation.isValid) {
      alert(phoneValidation.error);
      return false;
    }

    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    // Track that user has filled contact details and initiated payment
    trackEventWithUTM('spb_payment_initiated', 'seasonal_pooja_funnel', state?.selectedPlan, {
      amount: state?.finalAmount,
      member_count: 1 + (state?.familyMembers?.length ?? 0),
    });
    trackBeginCheckout(
      [{
        item_id: 'seasonal_pooja_navratri_2026',
        item_name: state?.occasionName,
        item_category: 'Seasonal Pooja',
        item_variant: state?.selectedPlan === 'international' ? 'International' : 'India',
        price: state?.finalAmount ?? 0,
        quantity: 1 + (state?.familyMembers?.length ?? 0),
      }],
      state?.finalAmount ?? 0
    );

    try {
      setIsProcessing(true);

      // Create payment order
      const paymentOrder = await seasonalPoojaService.createPaymentOrder(
        state.bookingUuid,
        customerEmail.trim() || undefined,
        customerPhone.trim() || undefined
      );

      console.log('💳 Payment order created:', paymentOrder);

      // Initialize Razorpay
      const options = {
        key: paymentOrder.razorpayKeyId,
        amount: paymentOrder.amount * 100, // Amount in paise
        currency: paymentOrder.currency,
        order_id: paymentOrder.razorpayOrderId,
        name: 'Antaryatra',
        description: `${state.occasionName} Booking`,
        image: '/images/logo.png',
        handler: async function (response: any) {
          console.log('✅ Razorpay payment captured:', response);

          // Keep the processing state active while we verify on the backend
          setIsProcessing(true);

          const successState = {
            bookingId: state.bookingUuid,
            seasonalPoojaName: state.occasionName,
            eventDate: state.preferredDate,
            eventTime: '',
            primaryMember: state.primaryMember,
            additionalMembers: state.familyMembers,
            totalAmount: state.finalAmount,
            customerName: customerName.trim(),
            customerEmail: customerEmail.trim(),
            customerPhone: customerPhone.trim(),
            paymentId: response.razorpay_payment_id,
            paymentOrderResponse: paymentOrder
          };

          try {
            await seasonalPoojaService.verifyPayment(
              state.bookingUuid,
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature
            );
            console.log('✅ Backend payment verification complete');
            navigate('/seasonal-pooja/success', { state: successState });
          } catch (error) {
            // verifyPayment failed — but Razorpay already captured the payment.
            // The backend will also receive a webhook confirming the capture,
            // so we still send the user to the success page.
            // The payment ID is logged so ops can reconcile manually if needed.
            console.error('⚠️ Backend verification call failed (payment was captured by Razorpay):', error);
            console.warn('Payment ID for reconciliation:', response.razorpay_payment_id);
            navigate('/seasonal-pooja/success', { state: successState });
          }
        },
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerPhone
        },
        theme: {
          color: '#ff6b35'
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);            trackEventWithUTM('spb_payment_cancelled', 'seasonal_pooja_funnel', state?.selectedPlan, {
              amount: state?.finalAmount,
            });            console.log('❌ Payment cancelled by user');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('❌ Error creating payment:', error);      trackEventWithUTM('spb_payment_api_error', 'seasonal_pooja_funnel', state?.selectedPlan, {
        error_message: error instanceof Error ? error.message : 'unknown',
        amount: state?.finalAmount,
      });      alert('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!state || !state.bookingUuid) {
    return null;
  }

  return (
    <div className="seasonal-pooja-payment">
      <div className="seasonal-payment-container">
        <div className="spp-topbar">
          <button
            className="spp-back-btn"
            onClick={() => navigate('/seasonal-pooja', { state: { openForm: true, selectedPlan: state?.selectedPlan } })}
            disabled={isProcessing}
          >
            ← Back
          </button>
          <h2>Complete Your Booking</h2>
          <span className="spp-occasion-name">{state.occasionName}</span>
        </div>

        <div className="seasonal-payment-content">
          {/* Booking Summary */}
          <div className="summary-section">
            <h2>📋 Booking Summary</h2>
            
            <div className="summary-card">
              <div className="summary-item">
                <span className="label">Event:</span>
                <span className="value">{state.occasionName}</span>
              </div>
              
              <div className="summary-item">
                <span className="label">📅 Date:</span>
                <span className="value">{formatDate(state.preferredDate)}</span>
              </div>
              
              <div className="summary-item">
                <span className="label">🕐 Time:</span>
                <span className="value">To be scheduled</span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-item">
                <span className="label">👥 Participants:</span>
                <div className="participants-list">
                  {getAllMembers().map((name, index) => (
                    <span key={index} className="participant-name">
                      {index + 1}. {name}
                    </span>
                  ))}
                </div>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-item total">
                <span className="label">Total Amount:</span>
                <span className="value">₹{state.finalAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="payment-form-section">
            <h2>💳 Payment Details</h2>
            
            <form onSubmit={(e) => { e.preventDefault(); handlePayment(); }}>
              <div className="form-group">
                <label htmlFor="customerName">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  id="customerName"
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your full name"
                  maxLength={100}
                  pattern="[a-zA-Z\s]*"
                  title="Customer name should contain only letters and spaces (max 100 characters)"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="customerEmail">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  id="customerEmail"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="Enter your email"
                  maxLength={254}
                  title="Please enter a valid email address (max 254 characters)"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="customerPhone">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  id="customerPhone"
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Enter your 10-digit phone number"
                  maxLength={10}
                  pattern="[6-9][0-9]{9}"
                  title="Phone number must be 10 digits starting with 6, 7, 8, or 9"
                  required
                />
              </div>

              <div className="payment-info">
                <p>💡 You will be redirected to a secure payment gateway</p>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : `Pay ₹${state.finalAmount.toLocaleString('en-IN')}`}
                </button>
              </div>

              <div className="payment-security-badges">
                <span className="payment-security-badge">
                  🔒 SSL Encrypted payment with Razorpay
                </span>
                <span className="payment-security-divider">|</span>
                <span className="payment-security-badge">
                  🛡️ 100% Secure Transaction
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalPoojaPayment;
